import { useEffect, useRef, useState, useCallback } from "react";
import { Box, Flex, IconButton, Spinner, Text } from "@chakra-ui/react";
import { Maximize2, Volume2, VolumeX } from "lucide-react";

interface VideoPlayerProps {
  playbackUri: string;
  streamName?: string;
  onExpand?: () => void;
  /** Compact mode for preview, expanded mode for modal */
  variant?: "compact" | "expanded";
  /** Callback when video metadata is loaded with dimensions */
  onVideoMetadata?: (width: number, height: number) => void;
}

type ConnectionState = "connecting" | "connected" | "failed" | "closed";

export function VideoPlayer({
  playbackUri,
  streamName,
  onExpand,
  variant = "compact",
  onVideoMetadata,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>("connecting");
  const [muted, setMuted] = useState(true);

  const isCompact = variant === "compact";

  const connect = useCallback(async () => {
    if (!videoRef.current) return;

    setConnectionState("connecting");

    const pc = new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    });
    pcRef.current = pc;

    pc.addTransceiver("video", { direction: "recvonly" });
    pc.addTransceiver("audio", { direction: "recvonly" });

    pc.ontrack = (event) => {
      if (videoRef.current && event.streams[0]) {
        videoRef.current.srcObject = event.streams[0];
      }
    };

    pc.onconnectionstatechange = () => {
      switch (pc.connectionState) {
        case "connected":
          setConnectionState("connected");
          break;
        case "failed":
          setConnectionState("failed");
          break;
        case "closed":
        case "disconnected":
          setConnectionState("closed");
          break;
      }
    };

    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const response = await fetch(playbackUri, {
        method: "POST",
        headers: {
          "Content-Type": "application/sdp",
        },
        body: offer.sdp,
      });

      if (!response.ok) {
        throw new Error(`WHEP request failed: ${response.status}`);
      }

      const answerSdp = await response.text();
      await pc.setRemoteDescription({
        type: "answer",
        sdp: answerSdp,
      });
    } catch (err) {
      console.error("WHEP connection error:", err);
      setConnectionState("failed");
      pc.close();
    }
  }, [playbackUri]);

  useEffect(() => {
    connect();

    return () => {
      if (pcRef.current) {
        pcRef.current.close();
        pcRef.current = null;
      }
    };
  }, [connect]);

  const handleRetry = () => {
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    connect();
  };

  const iconSize = isCompact ? 14 : 18;
  const textSize = isCompact ? "xs" : "sm";
  const spinnerSize = isCompact ? "sm" : "lg";

  return (
    <Box
      position="relative"
      bg="black"
      borderRadius={isCompact ? "md" : undefined}
      overflow="hidden"
      h={isCompact ? "full" : "auto"}
      w={isCompact ? "full" : "auto"}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={muted}
        onLoadedMetadata={(e) => {
          const video = e.currentTarget;
          onVideoMetadata?.(video.videoWidth, video.videoHeight);
        }}
        style={
          isCompact
            ? {
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: connectionState === "connected" ? "block" : "none",
              }
            : {
                maxWidth: "90vw",
                maxHeight: "80vh",
                display: connectionState === "connected" ? "block" : "none",
              }
        }
      />

      {connectionState === "connecting" && (
        <Flex
          position="absolute"
          inset="0"
          align="center"
          justify="center"
          direction="column"
          gap={isCompact ? "2" : "3"}
          bg="black"
        >
          <Spinner size={spinnerSize} color="gray.400" />
          <Text fontSize={textSize} color="gray.400">
            {isCompact ? "Connecting..." : "Connecting to stream..."}
          </Text>
        </Flex>
      )}

      {connectionState === "failed" && (
        <Flex
          position="absolute"
          inset="0"
          align="center"
          justify="center"
          direction="column"
          gap={isCompact ? "2" : "3"}
          bg="black"
          cursor="pointer"
          onClick={handleRetry}
        >
          <Text fontSize={textSize} color="gray.400">
            Stream unavailable
          </Text>
          <Text fontSize={textSize} color="blue.400">
            Click to retry
          </Text>
        </Flex>
      )}

      {connectionState === "closed" && (
        <Flex
          position="absolute"
          inset="0"
          align="center"
          justify="center"
          direction="column"
          gap={isCompact ? "2" : "3"}
          bg="black"
          cursor="pointer"
          onClick={handleRetry}
        >
          <Text fontSize={textSize} color="gray.400">
            Stream ended
          </Text>
          <Text fontSize={textSize} color="blue.400">
            Click to reconnect
          </Text>
        </Flex>
      )}

      {connectionState === "connected" && (
        <Flex
          position="absolute"
          bottom={isCompact ? "1" : "2"}
          left={isCompact ? "1" : "2"}
          right={isCompact ? "1" : "2"}
          justify="space-between"
          align="center"
          px={isCompact ? "1" : "2"}
        >
          {streamName ? (
            <Text
              fontSize={textSize}
              color="white"
              textShadow="0 1px 2px rgba(0,0,0,0.8)"
            >
              {streamName}
            </Text>
          ) : (
            <Box />
          )}
          <Flex gap="1">
            <IconButton
              aria-label={muted ? "Unmute" : "Mute"}
              variant="ghost"
              size={isCompact ? "xs" : "sm"}
              color="white"
              _hover={{ bg: "whiteAlpha.300" }}
              onClick={() => setMuted(!muted)}
            >
              {muted ? (
                <VolumeX style={{ height: iconSize, width: iconSize }} />
              ) : (
                <Volume2 style={{ height: iconSize, width: iconSize }} />
              )}
            </IconButton>
            {onExpand && (
              <IconButton
                aria-label="Expand video"
                variant="ghost"
                size={isCompact ? "xs" : "sm"}
                color="white"
                _hover={{ bg: "whiteAlpha.300" }}
                onClick={onExpand}
              >
                <Maximize2 style={{ height: iconSize, width: iconSize }} />
              </IconButton>
            )}
          </Flex>
        </Flex>
      )}
    </Box>
  );
}
